/* ==========================================================================
   ASSETS/NEWSROOM-BENTO.JS (Aceternity UI Feed Controller)
   ========================================================================== */

class NewsroomFeed extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.grid = this.querySelector('[data-newsroom-grid]');
    this.emptyState = this.querySelector('[data-newsroom-empty]');
    this.status = this.querySelector('[data-newsroom-status]');
    this.searchInput = this.querySelector('[data-newsroom-search]');

    this.copy = {
      filterEmpty: this.dataset.newsroomI18nFilterEmpty || 'No matching articles found.',
      loadMore: this.dataset.newsroomI18nLoadMore || 'Load More',
      resultsLabel: this.dataset.newsroomI18nResultsLabel || 'Search results',
      error: this.dataset.newsroomI18nError || 'An error occurred.'
    };

    this.activeFilter = 'all';
    this.searchQuery = '';
    this.searchTimer = null;
    this.loadController = null;

    this.addEventListener('click', this.handleClick);
    this.searchInput?.addEventListener('input', this.handleSearchInput);

    // Initial sync
    this.applyFilter('all');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.searchInput?.removeEventListener('input', this.handleSearchInput);
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.loadController?.abort();
  }

  handleClick = async (event) => {
    const filter = event.target.closest('[data-newsroom-filter]');
    if (filter && this.contains(filter)) {
      event.preventDefault();
      this.applyFilter(filter.dataset.newsroomFilter || 'all');
      return;
    }

    const loadMore = event.target.closest('[data-newsroom-more]');
    if (loadMore && this.contains(loadMore)) {
      event.preventDefault();
      await this.loadMore(loadMore);
    }
  };

  handleSearchInput = (event) => {
    const nextQuery = event.target.value.trim().toLocaleLowerCase();
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => {
      this.searchQuery = nextQuery;
      this.updateVisibleCards();
    }, 150);
  };

  applyFilter(contentType) {
    this.activeFilter = contentType;

    // Synchronize both aria-pressed and .is-active CSS class for Aceternity UI
    this.querySelectorAll('[data-newsroom-filter]').forEach((btn) => {
      const isMatch = (btn.dataset.newsroomFilter || 'all') === this.activeFilter;
      btn.setAttribute('aria-pressed', String(isMatch));
      btn.classList.toggle('is-active', isMatch);
    });

    this.updateVisibleCards();
  }

  updateVisibleCards() {
    const cards = [...this.querySelectorAll('[data-newsroom-card]')];
    let visibleCount = 0;

    cards.forEach((card) => {
      const isTypeMatch = this.activeFilter === 'all' || card.dataset.contentType === this.activeFilter;
      const isSearchMatch = !this.searchQuery || (card.dataset.searchText || '').toLocaleLowerCase().includes(this.searchQuery);
      const isVisible = isTypeMatch && isSearchMatch;

      card.hidden = !isVisible;

      if (isVisible) {
        visibleCount++;
        // Re-trigger subtle Aceternity entry animation
        card.style.animation = 'none';
        void card.offsetWidth;
        card.style.animation = 'aceFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards';
      }
    });

    // Toggle Empty State Box
    if (this.emptyState) {
      this.emptyState.hidden = visibleCount > 0;
    }

    // Accessibility Status Announcement
    if (this.status) {
      this.status.textContent = `${this.copy.resultsLabel}: ${visibleCount}`;
    }
  }

  async loadMore(link) {
    if (link.dataset.loading === 'true' || !this.grid) return;

    link.dataset.loading = 'true';
    const originalHTML = link.innerHTML; // Preserve button HTML structure and icons

    link.innerHTML = `<span>${this.copy.loadMore}…</span>`;

    this.loadController?.abort();
    this.loadController = new AbortController();

    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      const res = await fetch(url.toString(), { signal: this.loadController.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const html = await res.text();
      const nextDoc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = nextDoc.querySelector('[data-newsroom-grid]') || nextDoc.querySelector('.newsroom-bento-grid');
      const nextLink = nextDoc.querySelector('[data-newsroom-more]');

      if (nextGrid) {
        const fragment = document.createDocumentFragment();
        nextGrid.querySelectorAll('[data-newsroom-card]').forEach((card) => fragment.append(card));
        this.grid.append(fragment);
      }

      if (nextLink?.href) {
        link.href = nextLink.href;
      } else {
        link.closest('[data-newsroom-more-wrap]')?.remove();
      }

      this.updateVisibleCards();
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('Newsroom pagination error:', e);
      }
    } finally {
      if (link.isConnected) {
        link.dataset.loading = 'false';
        link.innerHTML = originalHTML; // Restore original icons and markup
      }
    }
  }
}

if (!customElements.get('newsroom-feed')) {
  customElements.define('newsroom-feed', NewsroomFeed);
}